import { useCallback, useEffect } from "react";

import Swal from "sweetalert2";

import { faLinkedin } from "@fortawesome/free-brands-svg-icons/faLinkedin";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { fetchApi } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";

export default function ConnectLinkedin() {
  const { account, mutateAccount } = useAuthentication();
  const { setNotification } = useNotification();
  const isConnected = account?.linkedinConnected;

  const onDisconnect = useCallback(async () => {
    try {
      const result = await Swal.fire({
        icon: "question",
        title: "Are you sure you want to disconnect your LinkedIn account?",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        confirmButtonColor: "#ac334b",
        customClass: {
          container: "z-[999999999]",
        },
      });

      if (!result.isConfirmed) return;

      await fetchApi("/api/v2/linkedin/disconnect", {
        method: "PATCH",
      });

      mutateAccount();
      setNotification("LinkedIn account disconnected successfully", "success");
    } catch (error) {
      setNotification("Failed to disconnect LinkedIn account", "error");
    }
  }, [mutateAccount, setNotification]);

  const onConnect = useCallback(() => {
    const params: any = {};
    params["connect-account"] = true;

    const paramStr = new URLSearchParams(params).toString();

    const url = `${emberAppBaseUrl}/api/v2/linkedin/auth?${paramStr}`;

    window.open(
      url,
      "_blank",
      "location=yes,height=570,width=520,scrollbars=yes,status=yes"
    );
  }, []);

  const onClick = useCallback(() => {
    if (isConnected) {
      return onDisconnect();
    }

    onConnect();
  }, [isConnected, onDisconnect, onConnect]);

  const onMessage = useCallback(
    async (event: any) => {
      try {
        const data = JSON.parse(event.data);

        await fetchApi("/api/v2/linkedin/connect", {
          method: "PATCH",
          body: JSON.stringify({
            "linkedin-id": data.linkedin_id,
            "access-token": data.access_token,
            "refresh-token": data.refresh_token,
            "expires-in": data.expires_in,
            "refresh-token-expires-in": data.refresh_token_expires_in,
            "connect-token": data.connect_token,
          }),
        });

        mutateAccount();
        setNotification("LinkedIn account connected successfully", "success");
      } catch (error) {
        console.error(error);
      }
    },
    [mutateAccount, setNotification]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage, false);

    return () => {
      window.removeEventListener("message", onMessage, false);
    };
  }, [onMessage]);

  return (
    <div>
      <button
        className="flex items-center gap-[10px] bg-linkedin text-white border-0 outline-none p-[10px] rounded-[5px]"
        onClick={onClick}
      >
        <FontAwesomeIcon icon={faLinkedin} className="text-[24px]" />
        {isConnected ? "Disconnect" : "Connect"} Linkedin
      </button>
    </div>
  );
}
